'use client';

import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import Link from 'next/link';
import type { FreeLesson, FreeLessonSection } from './lessonData';

interface FreeLessonContentProps {
  lesson: FreeLesson;
  onNext: () => void;
  isLastLesson: boolean;
}

// ─── Prompt copy block (dark navy) ───────────────────────────────────────────
function PromptCopyBlock({ content }: { content: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard not available
    }
  };

  return (
    <div className="rounded-xl p-5 relative" style={{ backgroundColor: '#1B2A4A' }}>
      <div className="flex items-start gap-3 mb-3">
        <span
          className="font-body text-xs uppercase tracking-widest font-semibold"
          style={{ color: 'rgba(255,255,255,0.45)', letterSpacing: '0.1em' }}
        >
          Try This Prompt
        </span>
      </div>
      <p
        className="font-body text-sm leading-relaxed mb-4 pr-10"
        style={{ color: 'rgba(255,255,255,0.88)', lineHeight: '1.72' }}
      >
        {content}
      </p>
      <div className="flex items-center justify-between">
        <p className="font-body text-xs italic" style={{ color: 'rgba(255,255,255,0.35)' }}>
          Copy this and paste it into any AI tool.
        </p>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 font-body text-xs font-semibold px-3 py-1.5 rounded-lg transition-all duration-200 active:scale-95"
          style={{
            backgroundColor: copied ? 'rgba(45,122,45,0.3)' : 'rgba(255,255,255,0.1)',
            color: copied ? '#7FBF7F' : 'rgba(255,255,255,0.7)',
            border: `1px solid ${copied ? 'rgba(45,122,45,0.4)' : 'rgba(255,255,255,0.15)'}`,
          }}
          aria-label={copied ? 'Copied' : 'Copy prompt'}
        >
          {copied ? <><Check size={13} aria-hidden="true" /> Copied</> : <><Copy size={13} aria-hidden="true" /> Copy</>}
        </button>
      </div>
    </div>
  );
}

// ─── Rich sections renderer (for 'main' blocks) ───────────────────────────────
function RichSections({ sections }: { sections: FreeLessonSection[] }) {
  return (
    <div className="flex flex-col gap-0">
      {sections.map((section, i) => (
        <React.Fragment key={i}>
          {section.dividerBefore && (
            <div className="my-5 h-px rounded-full" style={{ backgroundColor: '#D4891A', opacity: 0.35 }} aria-hidden="true" />
          )}
          {section.heading && (
            <h3
              className="font-body text-xs uppercase tracking-widest font-semibold mb-3"
              style={{ color: '#D4891A', letterSpacing: '0.12em' }}
            >
              {section.heading}
            </h3>
          )}
          {section.paragraphs.map((para, j) => (
            <p
              key={j}
              className="font-body text-sm leading-relaxed mb-3 last:mb-0"
              style={{ color: '#1B2A4A', lineHeight: '1.85' }}
            >
              {para}
            </p>
          ))}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Conversion card (end of Free Lesson 3) ──────────────────────────────────
function ConversionCard() {
  return (
    <div
      className="mt-10 rounded-2xl p-8"
      style={{ backgroundColor: '#FDF6EC', border: '1px solid rgba(212,137,26,0.25)' }}
    >
      <h3
        className="font-display font-semibold text-xl mb-5"
        style={{ color: '#1B2A4A', lineHeight: '1.3' }}
      >
        That&rsquo;s what the whole platform feels like.
      </h3>
      <p
        className="font-body text-sm mb-5"
        style={{ color: '#4B5563', lineHeight: '1.85' }}
      >
        Three lessons. Fifteen minutes or so.
      </p>
      <p
        className="font-body text-sm mb-5"
        style={{ color: '#4B5563', lineHeight: '1.85' }}
      >
        You know what AI is, you know the tools that matter, and you&rsquo;ve just used one on something real.
      </p>
      <p
        className="font-body text-sm mb-8"
        style={{ color: '#4B5563', lineHeight: '1.85' }}
      >
        If that felt useful — the rest of the platform is nine full modules of exactly this. Plain English, practical, at your level. Plus the Deep Dive Vault for the higher-stakes situations. And Anna&rsquo;s Bookshelf with the books and tools that actually helped.
      </p>
      <p
        className="font-body text-sm font-semibold mb-8"
        style={{ color: '#1B2A4A' }}
      >
        One payment. No subscription. Yours to keep.
      </p>
      <div className="flex flex-col gap-3 max-w-sm">
        <Link
          href="/conversion-page"
          className="block w-full text-center font-body text-sm font-semibold py-4 px-6 rounded-xl transition-all duration-200 active:scale-95"
          style={{ backgroundColor: '#D4891A', color: 'white' }}
        >
          Get Full Access — $67 →
        </Link>
        <p className="font-body text-xs text-center" style={{ color: '#9CA3AF' }}>
          $79 after launch pricing ends.
        </p>
        <Link
          href="/free-lesson-screen?preview=1-1"
          className="block w-full text-center font-body text-sm font-semibold py-4 px-6 rounded-xl transition-all duration-200 active:scale-95 hover:bg-opacity-5"
          style={{
            border: '2px solid #1B2A4A',
            color: '#1B2A4A',
            backgroundColor: 'transparent',
          }}
        >
          Or see a full module lesson first →
        </Link>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function FreeLessonContent({ lesson, onNext, isLastLesson }: FreeLessonContentProps) {
  return (
    <div className="flex-1 min-h-screen" style={{ backgroundColor: '#F9F7F4' }}>
      <div className="max-w-2xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        {/* Progress label */}
        <p className="font-body text-xs mb-6" style={{ color: '#888888' }}>
          Free Lesson {lesson.number} of 3
        </p>

        {/* Lesson header */}
        <div className="mb-10">
          <span
            className="inline-block font-body text-xs uppercase tracking-widest mb-3 font-semibold"
            style={{ color: '#D4891A', letterSpacing: '0.12em' }}
          >
            Free Lesson {lesson.number}
          </span>
          <h1
            className="font-display font-semibold mb-4"
            style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', color: '#1B2A4A', lineHeight: '1.2' }}
          >
            {lesson.title}
          </h1>
          <div className="h-px w-12 rounded-full" style={{ backgroundColor: '#D4891A' }} aria-hidden="true" />
        </div>

        {/* Lesson blocks */}
        <div className="flex flex-col gap-6">
          {lesson.blocks.map((block, i) => {
            // ── Situation card (grey) ──────────────────────────────────────
            if (block.type === 'situation') {
              return (
                <div key={i} className="rounded-xl p-6" style={{ backgroundColor: '#F0EDE8', border: '1px solid #E5E0D8' }}>
                  <span className="block font-body text-xs uppercase tracking-widest font-semibold mb-4" style={{ color: '#D4891A', letterSpacing: '0.12em' }}>The Situation</span>
                  {(block.content || '').split('\n\n').map((para, j) => (
                    <p key={j} className="font-body text-sm leading-relaxed mb-3 last:mb-0" style={{ color: '#1B2A4A', lineHeight: '1.85' }}>
                      {para}
                    </p>
                  ))}
                </div>
              );
            }

            // ── Main content card (white, multi-section) ──────────────────
            if (block.type === 'main' && block.sections) {
              return (
                <div key={i} className="rounded-xl p-6" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E0D8' }}>
                  <RichSections sections={block.sections} />
                </div>
              );
            }

            // ── Anna's Note card (warm cream) ─────────────────────────────
            if (block.type === 'anna') {
              return (
                <div key={i} className="rounded-xl p-6" style={{ backgroundColor: '#FDF6EC', border: '1px solid rgba(212,137,26,0.2)' }}>
                  <span className="block font-body text-xs uppercase tracking-widest font-semibold mb-4" style={{ color: '#D4891A', letterSpacing: '0.12em' }}>Anna&rsquo;s Note</span>
                  {(block.content || '').split('\n\n').map((para, j) => (
                    <p key={j} className="font-body text-sm leading-relaxed mb-3 last:mb-0" style={{ color: '#1B2A4A', lineHeight: '1.85' }}>
                      {para}
                    </p>
                  ))}
                  <p className="font-body text-sm font-medium mt-4 italic" style={{ color: '#D4891A' }}>— Anna</p>
                </div>
              );
            }

            // ── Try It Now card (light green + dark navy copy box) ─────────
            if (block.type === 'tryitnow') {
              return (
                <div key={i} className="rounded-xl p-6" style={{ backgroundColor: '#F0F7F0', border: '1px solid #C6E0C6' }}>
                  <span className="block font-body text-xs uppercase tracking-widest font-semibold mb-4" style={{ color: '#2D7A2D', letterSpacing: '0.12em' }}>Try It Now</span>
                  {(block.tryIntro || []).map((para, j) => (
                    <p key={j} className="font-body text-sm leading-relaxed mb-4" style={{ color: '#1B2A4A', lineHeight: '1.85' }}>
                      {para}
                    </p>
                  ))}
                  {block.promptContent && (
                    <div className="mb-4">
                      <PromptCopyBlock content={block.promptContent} />
                    </div>
                  )}
                  {(block.tryOutro || []).map((para, j) => (
                    <p key={j} className="font-body text-sm leading-relaxed mb-3 last:mb-0" style={{ color: '#1B2A4A', lineHeight: '1.85' }}>
                      {para}
                    </p>
                  ))}
                </div>
              );
            }

            // ── Next step card (light green, with amber CTA button) ────────
            if (block.type === 'nextstep') {
              return (
                <div key={i} className="rounded-xl p-6" style={{ backgroundColor: '#F0F7F0', border: '1px solid #C6E0C6' }}>
                  <span className="block font-body text-xs uppercase tracking-widest font-semibold mb-3" style={{ color: '#2D7A2D', letterSpacing: '0.12em' }}>Next</span>
                  {(block.content || '').split('\n\n').map((para, j) => (
                    <p key={j} className="font-body text-sm leading-relaxed mb-3" style={{ color: '#1B2A4A', lineHeight: '1.85' }}>
                      {para}
                    </p>
                  ))}
                  <div className="mt-5">
                    <button
                      onClick={onNext}
                      className="w-full font-body text-sm font-semibold py-3.5 px-6 rounded-xl transition-all duration-200 active:scale-95 text-center"
                      style={{ backgroundColor: '#D4891A', color: 'white' }}
                    >
                      {block.nextLabel || 'Next Lesson →'}
                    </button>
                    {block.nextSubtext && (
                      <p className="font-body text-xs text-center mt-2" style={{ color: '#9CA3AF' }}>
                        {block.nextSubtext}
                      </p>
                    )}
                  </div>
                </div>
              );
            }

            // ── Legacy block types ─────────────────────────────────────────
            if (block.type === 'prompt' && block.content) {
              return <PromptCopyBlock key={i} content={block.content} />;
            }
            if (block.type === 'why') {
              return (
                <div key={i} className="rounded-xl p-6" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E0D8' }}>
                  <span className="block font-body text-xs uppercase tracking-widest font-semibold mb-3" style={{ color: '#D4891A', letterSpacing: '0.12em' }}>Why This Matters</span>
                  <p className="font-body text-sm leading-relaxed" style={{ color: '#1B2A4A', lineHeight: '1.8' }}>{block.content}</p>
                </div>
              );
            }
            if (block.type === 'try') {
              return (
                <div key={i} className="rounded-xl p-6" style={{ backgroundColor: '#F0F7F0', border: '1px solid #C6E0C6' }}>
                  <span className="block font-body text-xs uppercase tracking-widest font-semibold mb-3" style={{ color: '#2D7A2D', letterSpacing: '0.12em' }}>Try It</span>
                  <p className="font-body text-sm leading-relaxed" style={{ color: '#1B2A4A', lineHeight: '1.8' }}>{block.content}</p>
                </div>
              );
            }

            return null;
          })}
        </div>

        {/* Conversion card after Free Lesson 3 */}
        {isLastLesson && <ConversionCard />}
      </div>
    </div>
  );
}
